import {
  streamcastAPI,
  m3castLightAPI,
  m3castHdmiAPI,
  STATUS,
  DEVICE_STATUS,
  DEVICE_TYPES,
  DEVICE_VARIANTS,
} from '@/api';

export const TYPES = {
  CHROMECAST: 'chromecast',
  APPLETV: 'appletv',
};

const FULL_DEVICE_SUPPORT = [
  DEVICE_TYPES.CHROMECAST,
  DEVICE_TYPES.APPLETV,
];
const FULL_VARIANT_SUPPORT = [
  DEVICE_VARIANTS.CENTRAL,
  DEVICE_VARIANTS.BUILTIN,
  DEVICE_VARIANTS.HDMI,
];

export default {
  namespaced: true,
  state: {
    loading: 0,
    errors: [],
    credentials: {},
    device: null,
    /**
     * later in time, the app list may be delivered from a server
     *
     * you can find more information about intents here:
     * - https://developer.chrome.com/docs/multidevice/android/intents/
     * - https://stackoverflow.com/a/39160933
     */
    apps: [
      {
        name: 'Netflix',
        icon: 'NetflixIcon',
        link: {
          web: 'https://netflix.com',
          android: 'intent://netflix.com/#Intent;package=com.netflix.mediaclient;scheme=nflx;S.browser_fallback_url=https://netflix.com;end',
          ios: 'https://netflix.com',
        },
        supported: [DEVICE_TYPES.CHROMECAST],
        supportedVariants: [DEVICE_VARIANTS.CENTRAL, DEVICE_VARIANTS.HDMI],
      },
      {
        name: 'Amazon Prime',
        icon: 'AmazonPrimeIcon',
        link: {
          web: 'https://watch.amazon.com',
          android: 'intent://app.primevideo.com/#Intent;package=com.amazon.avod.thirdpartyclient;scheme=primevideo;S.browser_fallback_url=https://play.google.com/store/apps/details?id=com.amazon.avod.thirdpartyclient;end;',
          ios: 'https://watch.amazon.com',
        },
        supported: FULL_DEVICE_SUPPORT,
        supportedVariants: FULL_VARIANT_SUPPORT,
      },
      {
        name: 'Disney+',
        icon: 'DisneyPlusIcon',
        link: {
          web: 'https://www.disneyplus.com',
          android: 'intent://disneyplus.com#Intent;package=com.disney.disneyplus;scheme=disneyplus;S.browser_fallback_url=https://www.disneyplus.com;end',
          ios: 'https://www.disneyplus.com',
        },
        supported: FULL_DEVICE_SUPPORT,
        supportedVariants: FULL_VARIANT_SUPPORT,
      },
    ],
  },
  getters: {
    isLoading: (state) => state.loading > 0,
    hasError: (state) => state.errors.length > 0,
    status: (state, { isLoading, hasError, device }) => {
      if (isLoading) {
        return STATUS.LOADING;
      }

      if (hasError) {
        const { errors } = state;
        for (let i = 0; i < errors.length; i += 1) {
          const error = errors[i];

          // wrong code / no device (404)
          if (error?.code === 404) {
            return STATUS.WRONG_CODE;
          }

          // fallback but also explicitly set for error.code 403
          return STATUS.MISSING_INFORMATION;
        }
      }

      if (device?.status === DEVICE_STATUS.USED) {
        return STATUS.READY;
      }

      return STATUS.INITIAL;
    },
    credentials: (state) => state.credentials,
    device: (state) => state.device,
    deviceType: (state) => state.device?.type || null,
    deviceTypeVariant: (state) => state.device?.variant || null,
    apps: ({ apps }, { deviceType, deviceTypeVariant }) => {
      if (
        !apps
        || !deviceType
        || !deviceTypeVariant
        || !FULL_DEVICE_SUPPORT.includes(deviceType)
        || !FULL_VARIANT_SUPPORT.includes(deviceTypeVariant)
      ) {
        return [];
      }

      return apps.filter((app) => app.supported.includes(deviceType)
        && app.supportedVariants.includes(deviceTypeVariant));
    },
  },
  mutations: {
    loading: (state, status) => {
      state.loading += (status) ? 1 : -1;
    },
    error: (state, error) => {
      if (error instanceof AggregateError) {
        error.errors.forEach((e) => {
          const { message } = e;
          const code = e.response?.status ?? e.code;
          state.errors.push({ code, message });
          console.log(state.errors);
        });
      } else {
        const { message } = error;
        const code = error.response?.status ?? error.code;
        state.errors.push({ code, message });
      }
    },
    clearErrors: (state) => {
      state.errors = [];
    },
    credentials: (state, credentials) => {
      state.credentials = credentials;
    },
    device: (state, device) => {
      state.device = device;
    },
  },
  actions: {
    authenticate: async ({ getters: { credentials }, commit }) => {
      try {
        commit('loading', true);
        const apiPromises = [
          { api: streamcastAPI, variant: DEVICE_VARIANTS.CENTRAL },
          { api: m3castLightAPI, variant: DEVICE_VARIANTS.BUILTIN },
          { api: m3castHdmiAPI, variant: DEVICE_VARIANTS.HDMI },
        ].map(
          ({ api, variant }) => api.authenticate(credentials).then((device) => ({
            ...device,
            variant,
          })),
        );
        const device = await Promise.any(apiPromises);
        commit('loading', false);
        commit('device', device);
        commit('clearErrors');
      } catch (error) {
        commit('loading', false);
        commit('error', error);
        throw error;
      }
    },
    /* TODO not in the scope of the MVP as of 2022-02-11
    releaseCurrentDevice: ({ getters: { device }, commit }) => {
      commit('loading', true);
      streamcastAPI.releaseDevice(device?.id)
        .then(() => commit('device', null))
        .catch((error) => commit('error', error))
        .finally(() => commit('loading', false));
    },
    */
  },
};
